import { useEffect, useState } from "react";

/**
 * `BeforeInstallPromptEvent` isn't yet in `lib.dom.d.ts` — it's a Chromium
 * extension to the standard event model. We type it loosely to keep the
 * compiler happy without bringing in `@types/wicg-mediasession` style
 * declarations.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

type DeviceFamily = "ios" | "android-chrome" | "desktop" | "other";

function detectDevice(): DeviceFamily {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent.toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(ua) || (ua.includes("mac") && navigator.maxTouchPoints > 1);
  if (isIos) return "ios";
  if (/android/.test(ua)) return "android-chrome";
  if (/windows|macintosh|linux/.test(ua)) return "desktop";
  return "other";
}

/**
 * Detect whether the page is being viewed as a launched PWA. Both the
 * iOS Safari property and the standard CSS media query are required —
 * iOS Safari has its own non-standard `navigator.standalone` flag.
 */
function detectStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const iosStandalone =
    "standalone" in window.navigator &&
    (window.navigator as { standalone?: boolean }).standalone === true;
  const displayStandalone = window.matchMedia?.("(display-mode: standalone)").matches ?? false;
  return Boolean(iosStandalone || displayStandalone);
}

/**
 * Hook backing the "Install Finally as an app" block on /me/account.
 *
 *   - `canPrompt` — Chrome/Edge fired `beforeinstallprompt`, we can call
 *     `promptInstall()` to show the native installer dialog.
 *   - `device` — coarse platform bucket, used to pick the right copy: on
 *     iOS we can't trigger the install, so we show the manual gesture
 *     (Share → Add to Home Screen).
 *   - `isStandalone` — already launched as a PWA; the block hides itself.
 *   - `promptInstall()` — returns the user's choice, or null if no prompt
 *     event was captured.
 */
export function usePwaInstall() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [device, setDevice] = useState<DeviceFamily>("other");
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    setDevice(detectDevice());
    setIsStandalone(detectStandalone());

    function onBeforeInstallPrompt(e: Event) {
      // Block Chrome's auto-prompt so we can surface it from our own UI.
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    }
    function onAppInstalled() {
      setInstallEvent(null);
      setIsStandalone(true);
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  async function promptInstall(): Promise<"accepted" | "dismissed" | null> {
    if (!installEvent) return null;
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    setInstallEvent(null);
    return choice.outcome;
  }

  return {
    /** True if the captured event is still valid and we can call `promptInstall()`. */
    canPrompt: Boolean(installEvent),
    device,
    /** True if the page is already running in standalone (= already installed). */
    isStandalone,
    promptInstall,
  };
}
